import clsx from "clsx"
import { isEmpty } from "lodash"
import PropTypes from "prop-types"
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd"
import { Controller, useFormContext } from "react-hook-form"
import { IoMdAddCircleOutline } from "react-icons/io"
import { IoRemoveCircleOutline } from "react-icons/io5"
import dragGray from "~/assets/Icons/noun_drag_3124730_gray.svg"
import NewDraftPublishedBox from "~/components/Common/NewDraftPublishedBox"

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}
const ResourceAdded = ({ id, className }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext()

  return (
    <Controller
      control={control}
      name={`${id}s`}
      render={({ field: { value, onChange, onBlur } }) => {
        function onDragEnd(result) {
          if (!result.destination) {
            return
          }

          if (result.destination.index === result.source.index) {
            return
          }

          const orderedResource = reorder(
            value,
            result.source.index,
            result.destination.index,
          )

          onChange(orderedResource)
        }

        return (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId={id}>
              {(provided) => {
                return (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {value?.map((resource, index) => {
                      const selected = value.find((c) => c.id === resource.id)
                      const imageKey = [
                        id === "path" ? "badge_image" : "thumbnail",
                      ]
                      const parsedImage =
                        resource[imageKey]?.secure_url ||
                        (!isEmpty(resource[imageKey]) &&
                          JSON.parse(resource[imageKey])?.secure_url)

                      return (
                        <Draggable
                          draggableId={resource.id.toString()}
                          index={index}
                          key={resource.id}
                        >
                          {(childProvided) => (
                            <div
                              ref={childProvided.innerRef}
                              {...childProvided.draggableProps}
                              key={resource.id}
                              className={clsx(
                                "border flex flex-col md:flex-row gap-1 py-2 md:py-0 md:gap-5 my-2 rounded-lg md:h-32 xl:min-h-20 items-center md:items-stretch px-1 md:px-0",
                                {
                                  "border-primary": selected,
                                  "border-gray3": !selected,
                                },
                              )}
                            >
                              {parsedImage ? (
                                <img
                                  src={parsedImage ?? ""}
                                  alt="logo"
                                  className="rounded-tl-lg rounded-bl-lg hidden md:block"
                                />
                              ) : (
                                <div
                                  className={clsx(
                                    "hidden md:block",
                                    id === "path" ? "w-[80px]" : "w-[1px]",
                                  )}
                                />
                              )}

                              <div className="flex justify-center flex-col items-center md:items-start flex-grow">
                                <h3 className="text-sm md:text-lg">
                                  {resource.name}
                                </h3>
                                {resource.version && (
                                  <p>Version: {resource.version}</p>
                                )}
                                <NewDraftPublishedBox
                                  published={resource?.published}
                                  has_been_published={
                                    resource?.has_been_published
                                  }
                                  archived={resource?.archived}
                                  size={"100px"}
                                />
                              </div>
                              <div className="flex items-center">
                                <button
                                  className="flex justify-center items-center bg-[#09295829] hover:bg-gray4 p-2 rounded-md h-10 xl:mr-3 w-28"
                                  onClick={() => {
                                    if (
                                      value.find((c) => c.id === resource.id)
                                    ) {
                                      onChange(
                                        value.filter(
                                          (c) => c.id !== resource.id,
                                        ),
                                      )
                                    } else {
                                      onChange(`${id}s`, [...value, resource])
                                    }
                                  }}
                                >
                                  {!selected ? (
                                    <IoMdAddCircleOutline
                                      className="w-6 h-6 mr-2"
                                      color="#092958"
                                    />
                                  ) : (
                                    <IoRemoveCircleOutline
                                      className="w-6 h-6 mr-2"
                                      color="#092958"
                                    />
                                  )}
                                  <p className="uppercase text-sm font-medium">
                                    {selected ? "Remove" : "Add"}
                                  </p>
                                </button>
                              </div>
                              <div
                                {...childProvided.dragHandleProps}
                                className="items-center px-4 hidden md:flex"
                              >
                                <img src={dragGray} alt="" width="20px" />
                              </div>
                            </div>
                          )}
                        </Draggable>
                      )
                    })}
                    {provided.placeholder}
                  </div>
                )
              }}
            </Droppable>
          </DragDropContext>
        )
      }}
    />
  )
}

ResourceAdded.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
}

export default ResourceAdded
